/*  iceberg-ui  */
import {
    BarChart,
    BarChartLayout,
    DonutChart,
    Flex,
    HalfPieChart,
    MultiLineChart,
    PieChart,
    ProgressCircle
} from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ChartTooltip } from 'recharts';

/*  internal  */
import {
    AxisXTick,
    AxisYTick,
    renderCustomVerticalLayoutLabel
} from '../Helpers/charts';
import { templateInterpolation } from '../Helpers/templateInterpolation';
import { type ChartPanel as ChartPanelType } from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

const ChartComponent = () => {
    const { t } = useTranslation();
    const {
        queryResults: { data = {} },
        panelState: { height },
        schema: { format }
    } = usePanelContext<ChartPanelType>();
    const { palette } = useTheme();

    const { series = [] } = data;

    if (format.isRiskChart) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        series.forEach((item: any) => {
            // eslint-disable-next-line no-param-reassign
            item.risk = item.risk.toLowerCase();
        });
    }

    switch (format.chartType) {
        case 'bar':
            return (
                <BarChart
                    data={series}
                    emptyMessage={t('Common.noData')}
                    enableXAxis={format.enableXAxis || true}
                    enableYAxis={format.enableYAxis || true}
                    isRiskChart={format.isRiskChart}
                    layout={format.orientation as BarChartLayout}
                    noLegend={format.noLegend}
                    responsiveContainerHeight={height}
                    barLabelFormatter={
                        format.orientation === 'vertical'
                            ? renderCustomVerticalLayoutLabel
                            : undefined
                    }
                    showXCartesianGrid={!(format.orientation === 'vertical')}
                    tooltip={Boolean(format.tooltip ?? true)}
                    tooltipLabelFormatter={(value: string) =>
                        templateInterpolation(
                            (typeof format.tooltip === 'object' &&
                                format.tooltip?.formatter) ||
                                '{{value}}',
                            {
                                value
                            }
                        )
                    }
                    tooltipUnits={format.units}
                    xAxisProps={{
                        allowDecimals: format.xAxis?.allowDecimals,
                        // We hide the x axis for vertical layout
                        hide: format.orientation === 'vertical',
                        interval: 0, // Show all ticks. If type is category, one could use index to reduce the number of ticks: https://github.com/recharts/recharts/discussions/3730
                        tick: AxisXTick,
                        type:
                            format.orientation === 'vertical'
                                ? 'number'
                                : 'category'
                    }}
                    yAxisProps={{
                        allowDecimals: format.yAxis?.allowDecimals,
                        label: {
                            angle: -90,
                            fill: palette.graph.axisLabels,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle' },
                            value: format.yAxis?.label
                        },
                        tick: AxisYTick,
                        type:
                            format.orientation === 'vertical'
                                ? 'category'
                                : 'number'
                    }}
                />
            );
        case 'donut':
            return (
                <DonutChart
                    cursor={format.cursor}
                    emptyMessage={t('Common.noData')}
                    data={series}
                    isRiskChart={format.isRiskChart}
                    noLegend={format.noLegend}
                    popoverContentsColumns={popoverContentsColumns}
                    responsiveContainerHeight={300}
                />
            );
        case 'halfPie':
            return (
                <HalfPieChart
                    cursor={format.cursor}
                    data={series}
                    emptyMessage={t('Common.noData')}
                    emptyMessageFontSize={FontSizes[1]}
                    isRiskChart={format.isRiskChart}
                    noLegend={format.noLegend}
                    outerRadius={90}
                    responsiveContainerHeight={150}
                    tooltip={
                        <ChartTooltip
                            allowEscapeViewBox={{ x: true, y: true }}
                            contentStyle={{ paddingBottom: 0, paddingTop: 0 }}
                            formatter={(value, text) => [
                                t('Risk.numberAppsAtRiskLevel', {
                                    text,
                                    value
                                }),
                                null
                            ]}
                            itemStyle={{
                                color: palette.text.primary,
                                margin: 0,
                                whiteSpace: 'normal',
                                width: '100px'
                            }}
                            wrapperStyle={{ padding: 0, zIndex: 1000 }}
                        />
                    }
                />
            );
        case 'progressCircle':
            return (
                <ProgressCircle
                    cursor={format.cursor}
                    data={series}
                    emptyMessage={t('Common.noData')}
                    variant={format.progressCircleVariant}
                    responsiveContainerHeight={120}
                    text={format.progressCircleText || data.series[0].value}
                    title={format.progressCircleTitle}
                    titleTooltipText={format.progressCircleTitleTooltip}
                />
            );
        case 'pie':
            return (
                <PieChart
                    cursor={format.cursor}
                    data={data.series}
                    emptyMessage={t('Common.noData')}
                    isRiskChart={format.isRiskChart}
                    noLegend={format.noLegend}
                    popoverContentsColumns={popoverContentsColumns}
                    responsiveContainerHeight={200}
                />
            );
        case 'line':
            return (
                <MultiLineChart
                    data={series}
                    emptyMessage={t('Common.noData')}
                    enableXAxis
                    enableYAxis
                    height={height || 100}
                    tooltip={Boolean(format.tooltip ?? true)}
                    width={100}
                    xAxisProps={{ tick: AxisXTick }}
                    yAxisProps={{
                        tick: AxisYTick
                    }}
                />
            );
        default:
            return null;
    }
};

/**
 * Renders a chart panel.
 * TODO: Implement the chart panel.
 *
 * @param panel - The panel to render.
 * @returns The rendered panel.
 */
export const ChartPanel = ({ panel }: { panel: ChartPanelType }) => (
    <Panel panel={panel}>
        <Flex
            data-testid="chartPanelComponentWrapper"
            sx={{
                alignItems: 'center',
                flex: 1,
                flexDirection: 'column',
                justifyContent: 'center'
            }}
        >
            <ChartComponent />
        </Flex>
    </Panel>
);
